<template>
  <UIButton
    v-if="isButtonVisible"
    v-bind="{
      class: 'asset-eod__button',
      label: buttonLabel,
      variant: 'secondary',
      size: 'sm',
    }"
    full
    @click="handleToggle"
  />
  <div v-if="isFieldVisible" class="asset-eod__field">
    <EODSearch
      ref="inputRef"
      v-model="securityValue"
      :disabled
      :search-on-load="isToggled"
      placeholder="Tap to search asset"
      @ready="handleReady"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, ref, useTemplateRef, watch } from 'vue'

import { AssetSecurityMaster } from '@types'
import { AssetClass } from '..'

import { ASSET_FIELD } from '../utils/const'
import { buildSecurityInfoTicker } from './utils/helpers'
import {
  ASSET_CRYPTO_CURRENCY_TYPE,
  ASSET_CURRENCY_DEPARTURE,
  ASSET_CURRENCY_TYPE,
} from '@/entities/assets/utils/const'

import { useAssetsStore } from '@/store/assets/index'

import EODSearch from '@/modules/EODSearch/EODSearch.vue'

import { UIButton } from '@ui/buttons'

type Props = {
  instance: AssetClass
  disabled?: boolean
  focusOnLoad?: boolean
  isToggled?: boolean
}

const props = defineProps<Props>()

const assetsStore = useAssetsStore()

const typeValue = props.instance.field<string>(ASSET_FIELD.TYPE)
const nameValue = props.instance.field<string>(ASSET_FIELD.NAME)
const tickerValue = props.instance.field<string>(ASSET_FIELD.TICKER)
const currencyValue = props.instance.field<string>(ASSET_FIELD.CURRENCY)
const securityInfoTickerValue = props.instance.field<string | null>(
  ASSET_FIELD.SECURITY_INFO_TICKER,
)
const linkedSecurityIdValue = props.instance.field<string | null>(
  ASSET_FIELD.LINKED_SECURITY_ID,
)

const inputRef = useTemplateRef('inputRef')

const securityValue = ref<AssetSecurityMaster>()

const toggledFlag = ref(!!props.isToggled)

const assetFormSaveFocus = inject<() => void>('asset-save-focus')

const isButtonVisible = computed(() => props.isToggled && toggledFlag.value)

const buttonLabel = computed(
  () =>
    `Search security master${
      nameValue.value ? ` for: ${nameValue.value}` : ''
    }`,
)

const isFieldVisible = computed(() => !toggledFlag.value)

const isCurrency = computed(() =>
  [ASSET_CURRENCY_TYPE, ASSET_CRYPTO_CURRENCY_TYPE].includes(typeValue.value),
)

const handleToggle = async () => {
  toggledFlag.value = !toggledFlag.value
  await nextTick()
  inputRef.value?.search(nameValue.value)
}

watch(securityValue, value => {
  if (value === undefined) return
  nameValue.value = value.name
  tickerValue.value = value.code
  if (assetsStore.getTypes.includes(value.type)) {
    typeValue.value = value.type
  }
  if (!isCurrency.value) {
    // OpenFIGI results carry no currency; US/CC listings are USD-denominated.
    currencyValue.value = value.currency ?? ASSET_CURRENCY_DEPARTURE
  }
  securityInfoTickerValue.value = buildSecurityInfoTicker(value)
  linkedSecurityIdValue.value = value.security_info_id ?? null
  assetFormSaveFocus && assetFormSaveFocus()
})

const handleReady = (el: HTMLInputElement) => {
  if (!props.focusOnLoad) return
  el.focus()
}
</script>

<style>
.asset-eod__button {
  @apply -mt-3;
}
</style>
